import { useState, useEffect } from "react";
import {
  Button,
  Card,
  Col,
  Form,
  Input,
  Row,
  Switch,
  Typography,
  notification,
  Space,
} from "antd";
import { useBoundStore } from "store";
import {
  updateTenantSettings,
  testSharePointConnection,
} from "tenants/services/TenantService";
import { ISharePointSettings } from "tenants/models";

export const SharePointConfig = () => {
  const [form] = Form.useForm();
  const [api, contextHolder] = notification.useNotification();
  const { tenant } = useBoundStore();
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);

  useEffect(() => {
    if (tenant?.settings?.sharepoint) {
      form.setFieldsValue(tenant.settings.sharepoint);
    }
  }, [tenant, form]);

  const onSave = async (values: ISharePointSettings) => {
    if (!tenant) return;
    setSaving(true);
    try {
      await updateTenantSettings(tenant.id, { sharepoint: values });
      api.success({
        message: "Settings Saved",
        description: "SharePoint integration settings have been updated.",
      });
    } catch (error: any) {
      api.error({
        message: "Error",
        description:
          error?.response?.data?.message ??
          error?.message ??
          "Failed to save settings",
      });
    } finally {
      setSaving(false);
    }
  };

  const onTestConnection = async () => {
    if (!tenant) return;
    const values = form.getFieldsValue();
    if (
      !values.aadClientId ||
      !values.aadClientSecret ||
      !values.aadTenantId ||
      !values.documentDriveId
    ) {
      api.warning({
        message: "Missing Fields",
        description:
          "Please fill in all required fields before testing the connection.",
      });
      return;
    }
    setTesting(true);
    try {
      const result = await testSharePointConnection(tenant.id, {
        aadClientId: values.aadClientId,
        aadClientSecret: values.aadClientSecret,
        aadTenantId: values.aadTenantId,
        documentDriveId: values.documentDriveId,
        graphApiUrl:
          values.graphApiUrl || "https://graph.microsoft.com/v1.0/",
      });
      if (result.success) {
        api.success({
          message: "Connection Successful",
          description: result.message,
        });
      } else {
        api.error({
          message: "Connection Failed",
          description: result.message,
        });
      }
    } catch (error: any) {
      api.error({
        message: "Connection Failed",
        description:
          error?.response?.data?.message ??
          error?.message ??
          "Failed to test connection",
      });
    } finally {
      setTesting(false);
    }
  };

  return (
    <div>
      {contextHolder}
      <Typography.Title level={4}>SharePoint Integration</Typography.Title>
      <Typography.Paragraph type='secondary'>
        Configure SharePoint folder creation for projects. When enabled, a
        folder structure is automatically created in SharePoint when a new
        project is added, and renamed when a project name changes.
      </Typography.Paragraph>
      <Form
        form={form}
        layout='vertical'
        onFinish={onSave}
        requiredMark={false}
        initialValues={{
          enabled: false,
          graphApiUrl: "https://graph.microsoft.com/v1.0/",
        }}
      >
        <Form.Item
          name='enabled'
          label='Enable SharePoint Integration'
          valuePropName='checked'
        >
          <Switch />
        </Form.Item>
        <Row gutter={16}>
          <Col span={12}>
            <Form.Item name='aadClientId' label='Azure AD Client ID'>
              <Input placeholder='Client ID from Azure AD app registration' />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item name='aadClientSecret' label='Azure AD Client Secret'>
              <Input.Password placeholder='Client secret' />
            </Form.Item>
          </Col>
        </Row>
        <Row gutter={16}>
          <Col span={12}>
            <Form.Item name='aadTenantId' label='Azure AD Tenant ID'>
              <Input placeholder='Azure AD tenant ID' />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item name='siteUrl' label='SharePoint Site URL'>
              <Input placeholder='https://yoursite.sharepoint.com/' />
            </Form.Item>
          </Col>
        </Row>
        <Row gutter={16}>
          <Col span={12}>
            <Form.Item name='documentDriveId' label='Document Drive ID'>
              <Input placeholder='SharePoint document library drive ID' />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item name='graphApiUrl' label='Graph API URL'>
              <Input placeholder='https://graph.microsoft.com/v1.0/' />
            </Form.Item>
          </Col>
        </Row>
        <Space>
          <Button
            type='primary'
            htmlType='submit'
            loading={saving}
          >
            Save Settings
          </Button>
          <Button onClick={onTestConnection} loading={testing}>
            Test Connection
          </Button>
        </Space>
      </Form>
    </div>
  );
};
